import React, { useState } from 'react';
import emailjs from '@emailjs/browser'; // ✅ ADDED
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditCard, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const CardApplication = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    cardType: '',
    additionalInfo: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      cardType: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.phone || !formData.country || !formData.city || !formData.cardType) {
      toast.error('Please fill in all required fields');
      return;
    }

    const templateParams = {
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      city: formData.city,
      cardType: formData.cardType,
      additionalInfo: formData.additionalInfo,
    };

    emailjs.send(
      'service_209amzm',   // 🔴 replace with your Service ID
      'template_qeclbxd',  // 🔴 replace with your Template ID
      templateParams,
      'qPr2EL7UeUnpHTVmg'    // 🔴 replace with your Public Key
    )
    .then(() => {
      setSubmitted(true);
      toast.success('Application sent to support@futuremoneysystem.ltd');
      
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          country: '',
          city: '',
          cardType: '',
          additionalInfo: '',
        });
      }, 3000);
    })
    .catch((error) => {
      console.error(error);
      toast.error('Failed to send. Try again.');
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] flex items-center justify-center px-4 pt-20">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 bg-[#FFD700] rounded-full flex items-center justify-center shadow-2xl shadow-[#FFD700]/50 animate-bounce">
              <CheckCircle className="w-12 h-12 text-[#0B2A4A]" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Application Submitted!</h2>
          <p className="text-xl text-gray-300 mb-8">
            Thank you for your interest. We'll review your application and contact you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#FFD700] rounded-full flex items-center justify-center shadow-2xl shadow-[#FFD700]/50">
              <CreditCard className="w-8 h-8 text-[#0B2A4A]" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Card Application
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            Complete the form below to apply for your premium financial card
          </p>
        </div>
      </section>

      {/* Application Form */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-white shadow-2xl border-2 border-gray-100">
              <CardContent className="p-8 md:p-12">
                <form onSubmit={handleSubmit} className="space-y-8">

                  {/* Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-lg font-semibold text-[#0B2A4A]">
                      Full Name *
                    </Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-lg font-semibold text-[#0B2A4A]">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john.doe@example.com"
                      className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-lg font-semibold text-[#0B2A4A]">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 123-4567"
                      className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                      required
                    />
                  </div>

                  {/* Country and City */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-lg font-semibold text-[#0B2A4A]">
                        Country *
                      </Label>
                      <Input
                        id="country"
                        name="country"
                        type="text"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="United States"
                        className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="city" className="text-lg font-semibold text-[#0B2A4A]">
                        City *
                      </Label>
                      <Input
                        id="city"
                        name="city"
                        type="text"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="New York"
                        className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                        required
                      />
                    </div>
                  </div>

                  {/* Card Type Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="cardType" className="text-lg font-semibold text-[#0B2A4A]">
                      Card Type *
                    </Label>
                    <Select onValueChange={handleSelectChange} value={formData.cardType}>
                      <SelectTrigger className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700]">
                        <SelectValue placeholder="Select card type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gold">Gold - Essential Benefits</SelectItem>
                        <SelectItem value="platinum">Platinum - Premium Benefits (Recommended)</SelectItem>
                        <SelectItem value="elite">Elite - Exclusive Benefits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-2">
                    <Label htmlFor="additionalInfo" className="text-lg font-semibold text-[#0B2A4A]">
                      Additional Information
                    </Label>
                    <Textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                      placeholder="Tell us more about your financial needs and preferences..."
                      className="min-h-32 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-6">
                    <Button
                      type="submit"
                      className="w-full bg-[#FFD700] text-[#0B2A4A] hover:bg-[#FFD700]/90 font-bold py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                    >
                      Submit Application
                    </Button>
                  </div>

                  {/* Disclaimer */}
                  <p className="text-sm text-gray-500 text-center mt-6 leading-relaxed">
                    By submitting this application, you agree to our terms of service and privacy policy. We will review your application and contact you within 2-3 business days.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      </AnimatedSection>

      {/* Benefits Reminder */}
      <AnimatedSection>
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-2xl p-8 md:p-12 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                Why Apply Today?
              </h2>
              <div className="grid md:grid-cols-3 gap-6 mt-8">
                <div className="text-white">
                  <div className="text-4xl font-bold text-[#FFD700] mb-2">2-3 Days</div>
                  <p className="text-gray-300">Quick Application Review</p>
                </div>
                <div className="text-white">
                  <div className="text-4xl font-bold text-[#FFD700] mb-2">24/7</div>
                  <p className="text-gray-300">Premium Support</p>
                </div>
                <div className="text-white">
                  <div className="text-4xl font-bold text-[#FFD700] mb-2">0%</div>
                  <p className="text-gray-300">Foreign Transaction Fees</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default CardApplication;